import { useEffect, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { AgentHandoff } from '../agent/AgentChatWidget';
import { loadConversationId } from '../agent/conversationId';

/** What Mia is doing while the summary generates. It takes several seconds on a cache miss, and
 *  a status that moves reads as work in progress where a static spinner reads as stuck. */
const SUMMARY_STEPS = [
  'Reading the score and what is firing…',
  'Going through the contact notes…',
  'Checking the plan, pipeline and opportunities…',
];
const STEP_MS = 4000;

interface SalesMiaSummaryProps {
  clientId: string;
  impersonateRole?: string;
  onContinue: (handoff: AgentHandoff) => void;
}

/**
 * Mia's 2-3 sentence read of the client at the top of the Snapshot, generated when the drawer
 * opens. One cached model call on the server, not a chat turn, so opening clients never takes the
 * rep's Mia slot. "Continue in Mia" seeds the summary into their thread first, so a follow-up
 * question has it.
 */
export function SalesMiaSummary({ clientId, impersonateRole, onContinue }: SalesMiaSummaryProps) {
  const [text, setText] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'done' | 'error'>('loading');
  const [step, setStep] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setPhase('loading');
    setText(null);
    setStep(0);
    const timer = window.setInterval(
      () => setStep((s) => Math.min(s + 1, SUMMARY_STEPS.length - 1)), STEP_MS,
    );
    siftApiClient.getSalesClientSummary(clientId, impersonateRole, controller.signal)
      .then((res) => {
        setText(res.text);
        setPhase('done');
      })
      .catch(() => {
        if (!controller.signal.aborted) setPhase('error');
      })
      .finally(() => window.clearInterval(timer));
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [clientId, impersonateRole, attempt]);

  const continueInMia = async () => {
    setContinuing(true);
    try {
      // The chat widget's own thread id, so the archived conversation starts from this summary.
      onContinue(await siftApiClient.continueClientSummary(clientId, loadConversationId('sales')));
    } catch {
      // Seeding failed: still open the chat on the summary, it just will not be in Mia's memory.
      if (text) onContinue({ prompt: 'Give me a quick read on this client and where I should focus.', reply: text });
    } finally {
      setContinuing(false);
    }
  };

  return (
    <section className="sv-mia-summary" aria-label="Mia summary" aria-busy={phase === 'loading'}>
      <div className="sv-mia-summary-head">
        <span className="sv-mia-badge">Mia</span>
        <span className="sv-mia-summary-title">Where to focus</span>
      </div>
      {phase === 'loading' && (
        <div className="sv-mia-summary-loading" role="status">
          <span className="sv-mia-dots" aria-hidden="true"><i /><i /><i /></span>
          <span>{SUMMARY_STEPS[step]}</span>
          <div className="sv-mia-skeleton" aria-hidden="true"><span /><span /><span /></div>
        </div>
      )}
      {phase === 'done' && text && <p className="sv-mia-summary-text">{text}</p>}
      {phase === 'error' && (
        <p className="sv-mia-summary-error">
          Mia could not put a summary together right now.{' '}
          <button type="button" className="sv-mia-link" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </button>
        </p>
      )}
      {phase === 'done' && text && (
        <button type="button" className="sv-popout-lazy-btn" disabled={continuing}
          onClick={() => void continueInMia()}>
          {continuing ? 'Opening Mia…' : 'Continue in Mia →'}
        </button>
      )}
    </section>
  );
}
